<!--
  记忆管理页面。
  用于查看、新增和删除当前用户的长期记忆，控制聊天时可注入的个人信息。
-->
<template>
  <div class="memory-page">
    <section class="memory-toolbar">
      <div>
        <h1>记忆管理</h1>
        <p>管理当前浏览器用户的长期记忆、偏好和人物信息。</p>
      </div>

      <div class="toolbar-actions">
        <el-switch
          v-model="pageMemoryEnabled"
          active-text="长期记忆开启"
          inactive-text="长期记忆关闭"
          @change="handleToggleMemory"
        />
        <el-button type="primary" :icon="Plus" @click="openCreateDialog">
          新增记忆
        </el-button>
        <el-button :icon="Refresh" @click="fetchMemories">刷新</el-button>
      </div>
    </section>

    <el-alert
      v-if="isMounted && !pageMemoryEnabled"
      class="memory-alert"
      title="长期记忆已关闭"
      type="warning"
      description="关闭后不会写入新记忆，也不会在聊天时检索旧记忆；已有记忆仍保留，可在此页面管理。"
      :closable="false"
      show-icon
    />

    <section class="memory-summary">
      <div class="summary-item">
        <span class="summary-value">{{ memories.length }}</span>
        <span class="summary-label">已保存记忆</span>
      </div>
      <div class="summary-item">
        <span class="summary-value">{{ preferenceCount }}</span>
        <span class="summary-label">偏好</span>
      </div>
      <div class="summary-item">
        <span class="summary-value">{{ isMounted ? currentAccountLabel : "-" }}</span>
        <span class="summary-label">当前账号</span>
      </div>
    </section>

    <section class="memory-filters">
      <el-select
        v-model="memoryFilters.kind"
        class="filter-kind"
        placeholder="全部类型"
        clearable
      >
        <el-option
          v-for="option in memoryKindFilterOptions"
          :key="option.value || 'all'"
          :label="option.label"
          :value="option.value"
        />
      </el-select>

      <el-input
        v-model="memoryFilters.keyword"
        class="filter-keyword"
        placeholder="搜索记忆内容"
        :prefix-icon="Search"
        clearable
      />

      <span class="filter-result">
        {{ filterResultText }}
      </span>

      <el-button v-if="hasActiveFilters" @click="resetMemoryFilters">
        重置
      </el-button>
    </section>

    <el-table
      v-loading="loading"
      class="memory-table"
      :data="filteredMemories"
      height="calc(100vh - 378px)"
      :empty-text="hasActiveFilters ? '没有匹配的记忆' : '暂无长期记忆'"
    >
      <el-table-column prop="kind" label="类型" width="120">
        <template #default="{ row }">
          <el-tag :type="kindTagType(row.kind)">{{ kindLabel(row.kind) }}</el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="content" label="内容" min-width="420">
        <template #default="{ row }">
          <div class="memory-content">{{ row.content }}</div>
        </template>
      </el-table-column>

      <el-table-column prop="createdAt" label="创建时间" width="190">
        <template #default="{ row }">
          {{ formatTime(row.createdAt) }}
        </template>
      </el-table-column>

      <el-table-column label="操作" width="110" fixed="right">
        <template #default="{ row }">
          <el-popconfirm
            title="删除这条记忆？"
            confirm-button-text="删除"
            cancel-button-text="取消"
            @confirm="deleteMemory(row.id)"
          >
            <template #reference>
              <el-button type="danger" :icon="Delete" circle />
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog v-model="createDialogVisible" title="新增偏好记忆" width="560px">
      <el-form label-position="top">
        <el-form-item label="类型">
          <el-segmented v-model="createForm.kind" :options="kindOptions" />
        </el-form-item>
        <el-form-item label="记忆内容">
          <el-input
            v-model="createForm.content"
            type="textarea"
            :rows="5"
            :maxlength="MEMORY_CONTENT_LIMITS.MAX_CHARS"
            show-word-limit
            placeholder="例如：我更喜欢你用中文回答，解释代码时先给结论再给细节。"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="creating"
          :disabled="!canSubmitMemory"
          @click="createMemory"
        >
          保存
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { ElMessage } from "element-plus";
import { Delete, Plus, Refresh, Search } from "@element-plus/icons-vue";
import { useAnonymousUser } from "~/composables/useAnonymousUser";
import { useMemorySettings } from "~/composables/useMemorySettings";
import {
  MEMORY_CONTENT_LIMITS,
  MEMORY_KIND_LABELS,
  MEMORY_KIND_TAG_TYPES,
  MEMORY_KIND_VALUES,
  MemoryKind,
} from "~/enums";

type MemoryRow = {
  id: string;
  userId: string;
  kind: MemoryKind;
  content: string;
  createdAt: number | string | Date;
};

type MemoryKindFilter = MemoryKind | "";

const { getAnonymousId } = useAnonymousUser();
const { user } = useAuthUser();
const { memoryEnabled, setMemoryEnabled } = useMemorySettings();

const isMounted = ref(false);
const pageMemoryEnabled = ref(true);
const loading = ref(false);
const creating = ref(false);
const memories = ref<MemoryRow[]>([]);
const createDialogVisible = ref(false);
const memoryFilters = reactive({
  kind: "" as MemoryKindFilter,
  keyword: "",
});
const createForm = reactive({
  kind: MemoryKind.PREFERENCE,
  content: "",
});

const currentUserId = computed(() => getAnonymousId());
const currentAccountLabel = computed(() => {
  if (user.value?.email) return user.value.email;
  if (user.value?.name) return user.value.name;
  return "匿名浏览器";
});
const preferenceCount = computed(
  () =>
    memories.value.filter((memory) => memory.kind === MemoryKind.PREFERENCE)
      .length,
);
const canSubmitMemory = computed(
  () =>
    createForm.content.trim().length >= MEMORY_CONTENT_LIMITS.MANUAL_MIN_CHARS,
);
const normalizedKeyword = computed(() =>
  memoryFilters.keyword.trim().toLowerCase(),
);
const memoryKindFilterOptions = computed(() => [
  { label: "全部类型", value: "" as MemoryKindFilter },
  ...MEMORY_KIND_VALUES.map((kind) => ({
    label: MEMORY_KIND_LABELS[kind],
    value: kind,
  })),
]);
const filteredMemories = computed(() =>
  memories.value.filter((memory) => {
    const matchesKind = !memoryFilters.kind || memory.kind === memoryFilters.kind;
    const matchesKeyword =
      !normalizedKeyword.value ||
      memory.content.toLowerCase().includes(normalizedKeyword.value);

    return matchesKind && matchesKeyword;
  }),
);
const hasActiveFilters = computed(
  () => Boolean(memoryFilters.kind) || Boolean(normalizedKeyword.value),
);
const filterResultText = computed(() => {
  if (!hasActiveFilters.value) return `共 ${memories.value.length} 条`;
  return `已筛选 ${filteredMemories.value.length} / 共 ${memories.value.length} 条`;
});

const kindOptions = [
  {
    label: MEMORY_KIND_LABELS[MemoryKind.PREFERENCE],
    value: MemoryKind.PREFERENCE,
  },
  { label: MEMORY_KIND_LABELS[MemoryKind.PROFILE], value: MemoryKind.PROFILE },
  { label: MEMORY_KIND_LABELS[MemoryKind.FACT], value: MemoryKind.FACT },
  { label: MEMORY_KIND_LABELS[MemoryKind.MESSAGE], value: MemoryKind.MESSAGE },
];

onMounted(() => {
  isMounted.value = true;
  pageMemoryEnabled.value = memoryEnabled.value;
  fetchMemories();
});

/**
 * 加载记忆
 */
async function fetchMemories() {
  const userId = currentUserId.value;
  if (!userId) return;

  loading.value = true;
  try {
    const response: any = await $fetch("/api/memory/list", {
      method: "GET",
      query: { userId },
    });
    memories.value = response.memories || [];
  } catch (error: any) {
    ElMessage.error(error?.message || "加载记忆失败");
  } finally {
    loading.value = false;
  }
}

/**
 * 新增记忆
 */
function openCreateDialog() {
  createForm.kind = MemoryKind.PREFERENCE;
  createForm.content = "";
  createDialogVisible.value = true;
}

/**
 * 保存新增的记忆
 */
async function createMemory() {
  const userId = currentUserId.value;
  const content = createForm.content.trim();
  if (!userId || !content) return;
  if (content.length < MEMORY_CONTENT_LIMITS.MANUAL_MIN_CHARS) {
    ElMessage.warning("记忆内容不能为空");
    return;
  }

  creating.value = true;
  try {
    await $fetch("/api/memory/create", {
      method: "POST",
      body: {
        userId,
        kind: createForm.kind,
        content,
      },
    });
    ElMessage.success("记忆已保存");
    createDialogVisible.value = false;
    await fetchMemories();
  } catch (error: any) {
    ElMessage.error(error?.statusMessage || error?.message || "保存记忆失败");
  } finally {
    creating.value = false;
  }
}

/**
 * 删除记忆
 */
async function deleteMemory(memoryId: string) {
  const userId = currentUserId.value;
  if (!userId || !memoryId) return;

  try {
    await $fetch("/api/memory/delete", {
      method: "POST",
      body: {
        userId,
        memoryId,
      },
    });
    memories.value = memories.value.filter((memory) => memory.id !== memoryId);
    ElMessage.success("记忆已删除");
  } catch (error: any) {
    ElMessage.error(error?.message || "删除记忆失败");
  }
}

function resetMemoryFilters() {
  memoryFilters.kind = "";
  memoryFilters.keyword = "";
}

/**
 * 长期记忆关闭/开启
 * @param value 
 */
function handleToggleMemory(value: boolean | string | number) {
  const enabled = Boolean(value);
  pageMemoryEnabled.value = enabled;
  setMemoryEnabled(enabled);
}

/**
 * 偏好记忆类型
 * @param kind 
 */
function kindLabel(kind: MemoryKind) {
  return MEMORY_KIND_LABELS[kind] || MEMORY_KIND_LABELS[MemoryKind.MESSAGE];
}

/**
 * 列表上的【类型】标签类型
 * @param kind 
 */
function kindTagType(kind: MemoryKind) {
  return MEMORY_KIND_TAG_TYPES[kind] || MEMORY_KIND_TAG_TYPES[MemoryKind.MESSAGE];
}

/**
 * 列表上的【创建时间】格式
 * @param kind 
 */
function formatTime(value: number | string | Date) {
  if (!value) return "-";
  return new Date(value).toLocaleString("zh-CN");
}
</script>

<style scoped lang="scss">
.memory-page {
  height: calc(100vh - 68px);
  overflow: hidden;
  padding: 24px;
  background: #f6f7f9;
}

.memory-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 16px;

  h1 {
    margin: 0 0 6px;
    font-size: 24px;
    color: #303133;
  }

  p {
    margin: 0;
    color: #606266;
    font-size: 14px;
  }
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.memory-alert {
  margin-bottom: 16px;
}

.memory-summary {
  display: grid;
  grid-template-columns: 160px 160px minmax(260px, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.summary-item {
  min-width: 0;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-value {
  color: #303133;
  font-size: 20px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-label {
  color: #909399;
  font-size: 13px;
}

.memory-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.filter-kind {
  width: 160px;
  flex: 0 0 160px;
}

.filter-keyword {
  width: 360px;
  max-width: 100%;
}

.filter-result {
  color: #909399;
  font-size: 13px;
  white-space: nowrap;
}

.memory-table {
  width: 100%;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}

.memory-content {
  line-height: 1.6;
  color: #303133;
  word-break: break-word;
}

@media (max-width: 900px) {
  .memory-page {
    padding: 16px;
    overflow-y: auto;
  }

  .memory-toolbar {
    flex-direction: column;
  }

  .toolbar-actions {
    justify-content: flex-start;
  }

  .memory-summary {
    grid-template-columns: 1fr;
  }

  .memory-filters {
    align-items: stretch;
    flex-direction: column;
  }

  .filter-kind,
  .filter-keyword {
    width: 100%;
    flex-basis: auto;
  }
}
</style>
